import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, MailCheck } from "lucide-react";

export default function ResetPasswordPage({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const isForgot = mode === "forgot";
  const title = isForgot ? "Mot de passe oublié" : mode === "activate" ? "Activation de votre compte" : "Nouveau mot de passe";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isForgot) {
      if (password !== confirm) {
        setError("Les deux mots de passe ne sont pas identiques.");
        return;
      }
      if (!token) {
        setError("Lien invalide : le jeton est manquant.");
        return;
      }
    }
    setLoading(true);
    try {
      if (isForgot) {
        await api.post("/auth/password/forgot", { email });
        setDone(true);
      } else {
        await api.post(mode === "activate" ? "/auth/activate" : "/auth/password/reset", { token, password });
        toast.success(mode === "activate" ? "Compte activé !" : "Mot de passe modifié !");
        setDone(true);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] p-6">
      <div className="w-full max-w-md animate-enter">
        <div className="flex items-center gap-3 mb-8">
          <img src="/assets/favicon.png" alt="Logo du Diocèse de Pamiers" className="h-14 w-14" />
          <div>
            <h1 className="text-2xl font-bold text-[#0369a1]" style={{ fontFamily: "Work Sans" }}>Notes de frais</h1>
            <p className="text-slate-500 font-medium">Diocèse de Pamiers</p>
          </div>
        </div>

        {done ? (
          <div className="card-base text-center" data-testid="reset-done-panel">
            <MailCheck className="h-16 w-16 text-[#15803d] mx-auto mb-4" aria-hidden="true" strokeWidth={2.5} />
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              {isForgot ? "Vérifiez votre boîte mail" : "C'est fait !"}
            </h2>
            <p className="text-slate-600 text-lg mb-6">
              {isForgot
                ? "Si ce compte existe, un email avec un lien de réinitialisation vient d'être envoyé."
                : "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe."}
            </p>
            <Link to="/connexion" className="btn-primary-lg w-full" data-testid="back-to-login-link">Retour à la connexion</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="card-base" data-testid="reset-form">
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">{title}</h2>
            <p className="text-slate-600 mb-6">
              {isForgot
                ? "Saisissez votre adresse email. Nous vous enverrons un lien pour choisir un nouveau mot de passe."
                : "Choisissez un mot de passe d'au moins 10 caractères, avec des lettres et des chiffres."}
            </p>
            <div className="space-y-5">
              {isForgot ? (
                <div>
                  <Label htmlFor="email" className="label-lg">Adresse email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="input-lg" placeholder="prenom.nom@diocese-pamiers.fr" data-testid="forgot-email-input" />
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="password" className="label-lg">Nouveau mot de passe</Label>
                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                      className="input-lg" placeholder="Au moins 10 caractères" data-testid="new-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="confirm" className="label-lg">Confirmez le mot de passe</Label>
                    <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      className="input-lg" placeholder="Saisissez-le à nouveau" data-testid="confirm-password-input" />
                  </div>
                </>
              )}
              {error && <p className="text-[#b91c1c] font-semibold" role="alert" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary-lg w-full" data-testid="reset-submit-button">
                <KeyRound className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} />
                {loading ? "Envoi en cours…" : isForgot ? "Envoyer le lien" : "Valider"}
              </button>
              <p className="text-center">
                <Link to="/connexion" className="text-[#0369a1] font-semibold underline underline-offset-4" data-testid="cancel-reset-link">
                  Retour à la connexion
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
